import { CSSProperties } from "react";
import { SystemStyleObject } from "@mui/system";
import { alpha, createTheme, Theme, ThemeOptions } from "@mui/material/styles";

/**
 * Neutrální tokeny moderního vzhledu - plochy zůstávají neutrální, barva značky (primary) se používá jen jako akcent.
 */
export const neutralColors = {
    background: "#F6F8FA",
    paper: "#FFFFFF",
    border: "#E4E7EC",
    textPrimary: "#101828",
    textSecondary: "#475467",
    textDisabled: "#98A2B3",
};

export const AppBarBackgroundColor = neutralColors.paper;
export const AppBarButtonActive = "rgba(20,119,202,0.08)";

/** Readonly pole - šedé pozadí a plný rámeček, aby bylo hned vidět, že se nedá upravit. */
export const readOnlyInputSx: SystemStyleObject<Theme> = {
    "& .MuiOutlinedInput-root": { backgroundColor: (theme) => theme.palette.grey[200] },
};

/**
 * Barevné zvýraznění stavových polí a chipů podle mockupu Licence v4.
 *
 * Hodnoty jsou z mockupu, ne z palety - jsou to odstíny MUI green/orange/red/blue 50-800, ale
 * mockup je zadal explicitně, takže je nepřepočítáváme na `theme.palette`, aby se vzhled
 * nerozešel se schválenou předlohou.
 */
export type HighlightTone = "green" | "amber" | "red" | "blue";

export const highlightColors: Record<HighlightTone, { background: string; border: string; text: string }> = {
    green: { background: "#E8F5E9", border: "#81C784", text: "#2E7D32" },
    amber: { background: "#FFF3E0", border: "#FFB74D", text: "#E65100" },
    red: { background: "#FFEBEE", border: "#E57373", text: "#C62828" },
    // Modrá v mockupu je jen u zkušební doby (chip a pruh status baru), sladěná s primary #1477CA.
    blue: { background: "#E3F2FD", border: "#90CAF9", text: "#00579B" },
};

/** Zvýrazněné readonly pole - nahrazuje šedý box barevným, jinak beze změny. */
export const highlightedInputSx = (tone: HighlightTone): SystemStyleObject<Theme> => ({
    background: highlightColors[tone].background,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: highlightColors[tone].border },
    "& .MuiInputBase-input": { color: highlightColors[tone].text, fontWeight: 600 },
});

const softShadow = "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)";
const popoverShadow = "0 12px 24px -6px rgba(16,24,40,0.12), 0 4px 8px -4px rgba(16,24,40,0.06)";

export const lightThemeOptions: ThemeOptions = {
    palette: {
        primary: {
            main: "#1477CA",
            light: "#4AA6FF",
            dark: "#00579B",
            contrastText: "#fff",
        },
        secondary: {
            main: "#1d1d1b",
            light: "#4b4b49",
            dark: "#000000",
            contrastText: "#fff",
        },
        error: {
            main: "#d32f2f",
            light: "#ff6659",
            dark: "#9a0007",
            contrastText: "#fff",
        },
        warning: {
            main: "#f9a825",
            light: "#ffdf5e",
            dark: "#c17900",
            contrastText: "#fff",
        },
        success: {
            main: "#388e3c",
            light: "#6abf69",
            dark: "#00600f",
            contrastText: "#fff",
        },
        info: {
            main: "#2196f3",
            light: "#6ec6ff",
            dark: "#0069c0",
            contrastText: "#fff",
        },
        grey: {
            100: "#f5f5f5",
            200: "#eeeeee",
            300: "#e0e0e0",
            400: "#bdbdbd",
            500: "#9e9e9e",
            600: "#757575",
            700: "#616161",
            800: "#424242",
            900: "#212121",
        },
        background: {
            default: neutralColors.background,
            paper: neutralColors.paper,
        },
        text: {
            primary: neutralColors.textPrimary,
            secondary: neutralColors.textSecondary,
            disabled: neutralColors.textDisabled,
        },
        divider: neutralColors.border,
    },
    shape: {
        borderRadius: 8,
    },
    typography: {
        fontFamily: "Inter, sans-serif",
        h4: { fontWeight: 600, letterSpacing: "-0.01em" },
        h5: { fontWeight: 600, letterSpacing: "-0.01em" },
        h6: { fontWeight: 600, fontSize: "1.0625rem" },
        subtitle1: { fontWeight: 600 },
        button: { fontWeight: 600, textTransform: "none" },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: { backgroundColor: neutralColors.background },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: ({ ownerState }) => ({
                    backgroundImage: "none",
                    // plochy bez stínu (karty, sekce) dostanou jemný rámeček; menu a dialogy si nechávají stín
                    ...(ownerState.variant === "elevation" &&
                        ownerState.elevation === 0 &&
                        !ownerState.square && {
                            border: `1px solid ${neutralColors.border}`,
                            borderRadius: 12,
                            boxShadow: softShadow,
                        }),
                }),
                // JmfPaper (elevation 5) a starší explicitní elevation 4 - stejná plochá karta jako elevation 0
                elevation4: { border: `1px solid ${neutralColors.border}`, borderRadius: 12, boxShadow: softShadow },
                elevation5: { border: `1px solid ${neutralColors.border}`, borderRadius: 12, boxShadow: softShadow },
                elevation8: { boxShadow: popoverShadow },
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
                color: "inherit",
            },
            styleOverrides: {
                root: {
                    backgroundColor: alpha(neutralColors.paper, 0.9),
                    backdropFilter: "saturate(180%) blur(8px)",
                    borderBottom: `1px solid ${neutralColors.border}`,
                },
                positionFixed: {
                    // spodní lišty detailu (Uložit, stránkování)
                    borderBottom: "none",
                    borderTop: `1px solid ${neutralColors.border}`,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                    borderRadius: 8,
                },
                outlined: {
                    borderColor: neutralColors.border,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { borderRadius: 8 },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 8,
                    backgroundColor: neutralColors.paper,
                    // needitovatelné pole je šedé, aby se na první pohled odlišilo
                    "&.Mui-disabled": { backgroundColor: theme.palette.grey[200] },
                }),
                notchedOutline: {
                    borderColor: "#D0D5DD",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontWeight: 500, borderRadius: 6 },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: { textTransform: "none", fontWeight: 600, minHeight: 44 },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: { minHeight: 44 },
            },
        },
        MuiDialog: {
            styleOverrides: {
                // dialog dostává fokus při otevření - bez výchozího focus ringu prohlížeče kolem celé karty
                paper: { borderRadius: 12, outline: "none" },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: { fontWeight: 600, fontSize: "1.125rem" },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: neutralColors.textPrimary,
                    fontSize: "0.75rem",
                    borderRadius: 6,
                },
                arrow: { color: neutralColors.textPrimary },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: { borderRadius: 10, border: `1px solid ${neutralColors.border}` },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: neutralColors.border },
            },
        },
        MuiGrid: {
            styleOverrides: {
                // Grid v MUI 7 (dřívější Grid2) už kontejneru nedává `width: 100%` jako GridLegacy - ve flex rodiči
                // (Toolbar dolních lišt detailu apod.) se pak kontejner smrskne na obsah a vše je zarovnané vlevo.
                // Kontejner, který je zároveň položkou (má `size`), si šířku řídí sám.
                container: ({ ownerState }) =>
                    Object.keys((ownerState as { size?: Record<string, unknown> }).size ?? {}).length === 0 ? { width: "100%" } : {},
            },
        },
    },
};

/**
 * Hotové téma bez lokalizace. Pro odvozené téma (např. s locale) používej `lightThemeOptions` –
 * předání hotového `Theme` do `createTheme` je pro TypeScript extrémně drahé (typová kontrola dev serveru pak nestačí s pamětí).
 */
export const lightTheme = createTheme(lightThemeOptions);

/** Barvy toastů (react-toastify) - odvozené z palety tématu. */
export const toastStyles: Record<"error" | "success" | "info", CSSProperties> = {
    error: { background: lightTheme.palette.error.main },
    success: { background: lightTheme.palette.success.main },
    info: { background: lightTheme.palette.primary.main },
};
